import { when } from 'mobx';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IOnboardingMilestone, IOnboardingMilestonesProgress } from '@feathr/blackbox';
import { EOnboardingTaskState, type OnboardingTask } from '@feathr/blackbox';
import { showError } from '@feathr/components';

import { useAccount, useFlags, useStore } from '../state';
import {
  type EOnboardingProgressiveFeatures,
  getMessage,
  getRequiredTaskOrMilestone,
  isMilestone,
  isTask,
} from './useOnboardingStatus.utils';

/** Get information for Task-dependent items */
function useOnboardingStatus(
  feature?:
    | EOnboardingProgressiveFeatures.Projects
    | EOnboardingProgressiveFeatures.Home
    | EOnboardingProgressiveFeatures.Community
    | EOnboardingProgressiveFeatures.ImportEmailData,
): Readonly<{
  isLoading: boolean;
  isMilestonesProgressLoading: boolean;
  task: OnboardingTask | undefined;
  milestonesProgress: IOnboardingMilestonesProgress;
  message?: string | undefined;
}>;
/** Get information for Milestone-dependent items */
function useOnboardingStatus(
  feature?:
    | EOnboardingProgressiveFeatures.PublishFirstAdCampaign
    | EOnboardingProgressiveFeatures.PublishFirstEmailCampaign,
): Readonly<{
  isLoading: boolean;
  isMilestonesProgressLoading: boolean;
  milestone: IOnboardingMilestone | undefined;
  milestonesProgress: IOnboardingMilestonesProgress;
  message?: string | undefined;
}>;
/** Handle undefined for recursive support. */
function useOnboardingStatus(feature?: EOnboardingProgressiveFeatures): Readonly<{
  isLoading: boolean;
  isMilestonesProgressLoading: boolean;
  task?: OnboardingTask;
  milestone?: IOnboardingMilestone;
  milestonesProgress: IOnboardingMilestonesProgress;
  message?: string;
}>;
/** Get information for features that have onboarding prerequisites.
 * See EOnboardingProgressiveFeatures for details. */
function useOnboardingStatus(feature?: EOnboardingProgressiveFeatures): Readonly<{
  isLoading: boolean;
  isMilestonesProgressLoading: boolean;
  task?: OnboardingTask;
  milestone?: IOnboardingMilestone;
  milestonesProgress: IOnboardingMilestonesProgress;
  message?: string;
}> {
  const account = useAccount();
  const { t } = useTranslation();
  const { enforceOnboarding, showOnboarding } = useFlags();
  const { OnboardingTasks } = useStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isMilestonesProgressLoading, setIsMilestonesProgressLoading] = useState(true);
  const [task, setTask] = useState<OnboardingTask>();
  const [milestone, setMilestone] = useState<IOnboardingMilestone>();
  const [milestonesProgress, setMilestonesProgress] = useState<IOnboardingMilestonesProgress>({
    completed: 0,
    total: 0,
  });
  const enabledByFlags = showOnboarding && enforceOnboarding;

  const getMilestonesProgress = useCallback(async (): Promise<void> => {
    try {
      const milestonesProgress = await OnboardingTasks.getMilestonesProgress();
      setMilestonesProgress(milestonesProgress);
      setIsMilestonesProgressLoading(false);
    } catch (error) {
      showError(error, t);
      setIsMilestonesProgressLoading(false);
    }
  }, [OnboardingTasks]);

  useEffect(() => {
    if (!feature) {
      getMilestonesProgress();
    }

    if (!account.isPlatformLite || !enabledByFlags || feature === undefined) {
      setIsLoading(false);
      setTask(undefined);
      setMilestone(undefined);
      return;
    }
    const query = getRequiredTaskOrMilestone(feature);

    async function fetchData(): Promise<void> {
      try {
        if (isTask(query)) {
          // Each task is a unique document, so we can reference the first one.
          const response = OnboardingTasks.list({ filters: { _cls: query } });
          await when(() => response.isLoaded);
          if (response.models.length > 0) {
            setTask(response.models[0]);
          } else {
            throw new Error(t('Task {{query}} not found', { query }));
          }
        } else if (isMilestone(query)) {
          const response = await OnboardingTasks.getMilestone(query);
          setMilestone(response);
        }
      } catch (error) {
        showError(error, t);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, []);

  if (feature === undefined) {
    return {
      isLoading,
      isMilestonesProgressLoading,
      task: undefined,
      milestone: undefined,
      milestonesProgress,
      message: undefined,
    };
  }

  return {
    isLoading,
    isMilestonesProgressLoading,
    task,
    milestone,
    milestonesProgress,
    message: [task?.get('status'), milestone?.state].includes(EOnboardingTaskState.Completed)
      ? undefined
      : getMessage(feature, t),
  };
}

export default useOnboardingStatus;
